import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { PricingForm } from "./PricingForm.tsx";

interface PricingStepProps {
  onNext: () => void;
}

const PricingStep: React.FC<PricingStepProps> = ({ onNext }) => {
  const { t } = useTranslation("help");

  // Animation variants for the tip cards
  const tipVariants = {
    initial: { opacity: 0, scale: 0.9 },
    animate: (custom: number) => ({
      opacity: 1,
      scale: 1,
      transition: { delay: 0.1 * custom, duration: 0.3 },
    }),
  };

  const tips = [
    {
      title: t("tutorWithoutPricing.step1.title", "Welcome to Pricing Setup"),
      description: t(
        "tutorWithoutPricing.step1.description",
        "Setting up your pricing is essential for starting your tutoring journey.",
      ),
    },
    {
      title: t("tutorWithoutPricing.step2.title", "Default Pricing Structure"),
      description: t(
        "tutorWithoutPricing.step2.description",
        "Set your base hourly rate that applies to all lessons.",
      ),
    },
    {
      title: t("tutorWithoutPricing.step3.title", "Flexible Pricing Options"),
      description: t(
        "tutorWithoutPricing.step3.description",
        "Create different rates for specific subjects or curriculum levels.",
      ),
    },
    {
      title: t("tutorWithoutPricing.step4.title", "Change Anytime"),
      description: t(
        "tutorWithoutPricing.step4.description",
        "You can always adjust your pricing from calendar settings.",
      ),
    },
  ];

  return (
    <div className="relative flex h-full w-full items-center justify-center p-2 sm:p-4">
      <div className="relative z-10 grid h-full w-full max-w-7xl grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left side: Tips (desktop/tablet) */}
        <div className="hidden lg:col-span-1 lg:flex lg:flex-col lg:justify-center lg:space-y-4">
          {tips.map((tip, index) => (
            <motion.div
              key={index}
              animate="animate"
              className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
              custom={index + 1}
              initial="initial"
              variants={tipVariants}
            >
              <div
                className={`duration-0.75 absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors ${
                  index === 0
                    ? "from-green-500/20 to-emerald-500/20"
                    : index === 1
                      ? "from-blue-500/20 to-cyan-500/20"
                      : index === 2
                        ? "from-purple-500/20 to-violet-500/20"
                        : "from-orange-500/20 to-amber-500/20"
                }`}
              />
              <div className="relative z-50 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm leading-tight font-semibold">{tip.title}</h3>
                  <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                    {tip.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Right side: Pricing Form */}
        <div className="col-span-1 flex items-center justify-center lg:col-span-2">
          <div className="w-full">
            <PricingForm onComplete={onNext} />
          </div>
        </div>

        {/* Tips on mobile (moved below the form) */}
        <div className="lg:hidden">
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {tips.map((tip, index) => (
                <motion.div
                  key={index}
                  animate="animate"
                  className="bg-foreground/30 relative overflow-hidden rounded-xl p-4 backdrop-blur-sm"
                  custom={index + 1}
                  initial="initial"
                  variants={tipVariants}
                >
                  <div
                    className={`absolute top-1/2 left-0 h-full w-full -translate-x-1/6 -translate-y-1/2 rounded-full bg-gradient-to-br blur-3xl transition-colors duration-700 ${
                      index === 0
                        ? "from-green-500/20 to-emerald-500/20"
                        : index === 1
                          ? "from-blue-500/20 to-cyan-500/20"
                          : index === 2
                            ? "from-purple-500/20 to-violet-500/20"
                            : "from-orange-500/20 to-amber-500/20"
                    }`}
                  />
                  <div className="relative z-50 flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <h3 className="text-sm leading-tight font-semibold">{tip.title}</h3>
                      <p className="text-muted-foreground mt-1 text-xs leading-relaxed">
                        {tip.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingStep;
